import { FC, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import { useTranslate } from 'hooks/useTranslate';

import {
  IncentiveOfferType,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';

import { useIncentiveOfferOperationDetail } from '../context/IncentiveOfferOperationDetailContext';
import getOfferReference from '../../../utils/getOfferReference';

type PaymentDataRowProps = {
  label: string;
  value: string;
  copyLabel: string;
  hasBorderBottom?: boolean;
};

const PaymentDataRow: FC<PaymentDataRowProps> = ({
  label,
  value,
  copyLabel,
  hasBorderBottom = true,
}) => {
  const { t } = useTranslate();

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(value);
    Swal.fire({
      toast: true,
      position: 'top-end',
      timer: 2000,
      timerProgressBar: true,
      showConfirmButton: false,
      icon: 'success',
      title: t('CopiedToClipboard'),
    });
  }, [value, t]);

  return (
    <div className="col-12">
      <div
        className={`d-flex justify-content-between align-items-center py-1 ${
          hasBorderBottom ? 'border-bottom' : ''
        }`}
      >
        <span className="text-soft">{label}</span>
        <div className="d-inline-flex align-items-center">
          <strong>{value}</strong>
          <button
            type="button"
            className="btn btn-trigger btn-icon btn-sm text-primary"
            title={`${copyLabel}`}
            aria-label={`${copyLabel}`}
            onClick={handleCopy}
          >
            <em className="icon ni ni-copy" />
          </button>
        </div>
      </div>
    </div>
  );
};

const IncentiveOfferOperationDetailPaymentCard: FC = () => {
  const { t } = useTranslate();
  const { data } = useIncentiveOfferOperationDetail();
  const offerCreator = useMemo((): Partial<IncentivesMarketBeneficiary> | null => {
    if (!data.operation || !data.incentiveOffer) return null;

    const isSellOffer = data.incentiveOffer.type === IncentiveOfferType.SELL;
    const party = isSellOffer ? data.operation.seller : data.operation.buyer;

    if (!party || typeof party === 'string') return null;
    return party as Partial<IncentivesMarketBeneficiary>;
  }, [data.operation, data.incentiveOffer]);

  const offerReference = useMemo(
    () => getOfferReference(data.incentiveOffer),
    [data.incentiveOffer]
  );

  const operationType = useMemo(() => {
    if (!data.incentiveOffer) return '';
    return data.incentiveOffer.type === IncentiveOfferType.BUY
      ? t('Buy').toLowerCase()
      : t('Sale').toLowerCase();
  }, [data.incentiveOffer, t]);

  const paymentConcept = useMemo(
    () => `${t('Operation')} ${operationType} ${offerReference}`,
    [t, operationType, offerReference]
  );

  const accountNumber = offerCreator?.bankAccountNumber || '-';
  const partnerName = offerCreator?.name || '-';

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner py-3">
        <h6 className="card-title mb-2 d-flex align-items-center justify-content-between">
          <span>
            <em className="icon ni ni-cc-alt2 text-primary mr-2" />
            {t('PaymentData')}
          </span>
          <span className="badge badge-dim badge-warning">
            <em className="icon ni ni-alert-circle mr-1" />
            {t('Pending')}
          </span>
        </h6>

        {data.operationStatus === 'PAYMENT_PENDING' && (
          <div className="alert alert-warning py-2 px-3 mb-2">
            <div className="d-flex align-items-center fs-12px">
              <div
                className="spinner-border spinner-border-sm text-warning mr-2"
                role="status"
                aria-hidden="true"
              />
              <span>
                <strong>{t('PendingPayment')}:</strong>{' '}
                {t('PendingPaymentDescription')}
              </span>
            </div>
          </div>
        )}

        <div className="row fs-13px">
          <PaymentDataRow
            label={t('SellerAccountNumber')}
            value={accountNumber}
            copyLabel={t('CopyAccountNumber')}
          />
          <PaymentDataRow
            label={t('SellerName')}
            value={partnerName}
            copyLabel={t('CopySellerName')}
          />
          <PaymentDataRow
            label={t('Concept')}
            value={paymentConcept}
            copyLabel={t('CopyConcept')}
            hasBorderBottom={false}
          />
        </div>
      </div>
    </div>
  );
};

export default IncentiveOfferOperationDetailPaymentCard;
